import React, { useEffect, useState } from "react";
import {
  getAttemptDetails,
  GetSFMDisplay,
  resolveMismatchColumn,
  FormatWithCommas,
  displayAttributesTooltip,
} from "@helpers/helpers";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "@components/LoaderComponent/loader";
import TooltipComponent from "@components/TooltipComponent";
import SfmModalStore from "@stores/SfmModalStore";
import { ZERO_VALUE } from "@utils/constants";
import {
  ACTUAL_INDEX,
  LAST_ATTEMPT_INDEX,
  SINGLE_RECORD,
  FIRST_ELEMENT,
  TOOLTIP_TIMEOUT,
  SECTION_BASIC_INFO,
  SECTION_CORRECTIVE_ACTION,
  REDIRECT_PATHS,
} from "./constants";
import { createAttemptDetailsColumns } from "./columnsConfig";
import NoDataComponent from "@helpers/NoDataComponent";
import ExpandableSection from "./ExpandableSection";
import RecordsTable from "./RecordsTable";
import AttemptSelector from "./AttemptSelector";
import { ArrowLeft } from "lucide-react";

const AttemptDetails = ({ sentActivityTag, id: passedId, from }) => {
  const [attemptDetails, setAttemptDetails] = useState({});
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [recordId, setRecordId] = useState(null);
  const [showSection, setShowSection] = useState([SECTION_BASIC_INFO]);
  const [currentTooltip, setCurrentTooltip] = useState(null);
  const location = useLocation();
  const { t: translate, i18n } = useTranslation();

  useEffect(() => {
    // New way to extract the ID using URLSearchParams
    const queryParams = new URLSearchParams(location.search);
    const id = passedId || queryParams.get("id"); // Assuming the ID is passed as a query parameter named "id"

    // Use the extracted ID as needed, for example:
    getAttemptDetails(id, sentActivityTag, (attemptDetailsInfo) => {
      if (attemptDetailsInfo?.Attempts) {
        setAttemptDetails(attemptDetailsInfo);
        const lastAttemptId =
          attemptDetailsInfo.Attempts.length - LAST_ATTEMPT_INDEX;
        setSelectedAttempt(attemptDetailsInfo.Attempts[lastAttemptId]);
        setRecordId(id);
        if (attemptDetailsInfo.Attempts[lastAttemptId]["CorrectiveAction"]) {
          setShowSection((prevSections) => [
            ...prevSections,
            SECTION_CORRECTIVE_ACTION,
          ]);
        }
        setIsLoading(false);
      } else {
        const sfmData = GetSFMDisplay(attemptDetailsInfo.SFM);
        SfmModalStore.open(sfmData, () => {
          window.location.href = REDIRECT_PATHS[from];
        });
      }
    });
  }, [location, sentActivityTag, passedId]);

  const columns = createAttemptDetailsColumns(
    translate,
    setCurrentTooltip,
    TOOLTIP_TIMEOUT
  );

  const handleToggleSection = (identifier) => {
    const isExpanded = showSection.includes(identifier);
    setShowSection(
      isExpanded
        ? showSection.filter((item) => item !== identifier)
        : [...showSection, identifier]
    );
  };

  const attempts = attemptDetails?.Attempts;
  return isLoading ? (
    <Loader dots withContainer />
  ) : (
    <div>
      <div className="text-2xl dark:text-white py-5 px-4 font-bold w-full text-center flex items-center gap-2">
        <ArrowLeft
          className="dark:text-white"
          onClick={() => {
            window.location.href = REDIRECT_PATHS.exception;
          }}
        />
        {from === "exception"
          ? translate("attempt-details.exceptionTitle")
          : translate("attempt-details.inTransitTitle")}
      </div>

      {selectedAttempt && (
        <div className="border-2 border-gray-200 rounded mr-5 text-black dark:border-gray-700">
          <AttemptSelector
            attempts={attempts}
            selectedAttempt={selectedAttempt}
            onSelectAttempt={(index) =>
              setSelectedAttempt(attemptDetails.Attempts[index])
            }
          />
          <span className="text-xl dark:text-white pt-10 px-4 font-bold w-full text-center flex">
            {translate("attempt-details.attempt")}{" "}
            {attemptDetails.Attempts.indexOf(selectedAttempt) + ACTUAL_INDEX}
          </span>
          <ExpandableSection
            identifier={SECTION_BASIC_INFO}
            title={translate("attempt-details.section.BasicInfo")}
            data={{ AttemptType: "", ...selectedAttempt }}
            allowedKeys={[
              "AttemptType",
              "BusinessDay",
              "CreationDate",
              "MatchingResultName",
              "RecordMatchingKeys",
              "Mismatches",
            ]}
            displayValues={{
              AttemptType: translate("attempt-details.section.AttemptType"),
              BusinessDay: translate("attempt-details.section.BusinessDay"),
              CreationDate: translate("attempt-details.section.CreationDate"),
              MatchingResultName: translate(
                "attempt-details.section.MatchingResultName"
              ),
              RecordMatchingKeys: translate(
                "attempt-details.section.RecordMatchingKeys"
              ),
              Mismatches: translate("attempt-details.section.Mismatches"),
            }}
            customValues={{
              AttemptType: (
                <div className="flex flex-col gap-2">
                  <span className="">
                    {selectedAttempt.MatchingSource.MatchingSourceName}
                    {selectedAttempt.RunId ? (
                      <>
                        {" "}
                        {"("}
                        {translate("shared.runId")}
                        {":"} {selectedAttempt.RunId}
                        {")"}
                      </>
                    ) : null}
                  </span>
                </div>
              ),
              BusinessDay: `${selectedAttempt.BusinessDay} ${selectedAttempt.BDInstance}`,
              RecordMatchingKeys: selectedAttempt.RecordMatchingKeys.join(),
              Mismatches: resolveMismatchColumn(selectedAttempt.Mismatches),
            }}
            expanded={showSection.includes(SECTION_BASIC_INFO)}
            onToggle={() => handleToggleSection(SECTION_BASIC_INFO)}
          />
          {selectedAttempt["CorrectiveAction"] && (
            <ExpandableSection
              identifier={SECTION_CORRECTIVE_ACTION}
              title="Corrective Action"
              data={selectedAttempt["CorrectiveAction"]}
              allowedKeys={[
                "ActionTypeName",
                "ActionTypeDescription",
                "ActionDate",
                "ActionReasonName",
                "ActionReasonDescription",
                "ResultantClearedMatchingRecordCount",
              ]}
              displayValues={{
                ActionTypeName: "Action Type",
                ActionTypeDescription: "Action Description",
                ActionDate: "Action Date",
                ActionReasonName: "Action Reason",
                ActionReasonDescription: "Reason Description",
                ResultantClearedMatchingRecordCount: "Cleared Matching Records",
              }}
              customValues={{}}
              expanded={showSection.includes(SECTION_CORRECTIVE_ACTION)}
              onToggle={() => handleToggleSection(SECTION_CORRECTIVE_ACTION)}
            />
          )}
          <span
            className={`title flex gap-2 items-center font-semibold dark:text-white text-gray-500 text-md px-5 pt-10`}
          >
            {translate("attempt-details.matched-records.title")}
          </span>
          <div className="p-5 w-full flex flex-col gap-2">
            <RecordsTable
              attemptDetails={attemptDetails}
              selectedAttempt={selectedAttempt}
              columns={columns}
              recordType="source"
            />
            <RecordsTable
              attemptDetails={attemptDetails}
              selectedAttempt={selectedAttempt}
              columns={columns}
              recordType="destination"
            />
          </div>
          <TooltipComponent
            anchorElement={"attributes-tooltip"}
            toolTipMessage={displayAttributesTooltip(currentTooltip)}
            position="top"
            className="max-w-max z-50 !bg-gray-500"
          />
        </div>
      )}
    </div>
  );
};
export default AttemptDetails;
